import { useLocation, useParams } from 'react-router-dom';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { useEffect, useState } from 'react';
import { useAnalysisData, useMessage } from 'pages/trading/queryHooks';
import { PageContainer, PageSectionTitle, Tile, TileContent } from 'components/Layout/PageContainer';
import { Box, Container } from '@mui/material';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { TradingMessageDisplayTile } from 'pages/trading/TradingMessageDisplay';
import { MarketshareCategoryEvent } from 'pages/trading/model';
import { BarChartConfig } from 'components/chart/data/BarChartConfig';
import { MetricsChart } from 'components/MetricsChart';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { ColumnChartRenderer } from 'components/chart/renderer/ColumnChartRenderer';
import { ChartData } from 'components/chart/data/ChartData';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { getErrorMessage } from 'utils/getErrorMessage';
import { Select } from 'components/select/Select';
import { FlatSelectContent } from 'components/select/FlatSelectContent';
import type { SelectOption } from 'components/select/types';
import { useSelectClose } from 'components/select/SelectContext';

interface MarketShareSelectProps {
  id: string;
  label: string;
  options: SelectOption<string>[];
  value: string | undefined;
  onChange: (value: string) => void;
}

function MarketShareOptions({
  id,
  options,
  value,
  onChange,
}: Pick<MarketShareSelectProps, 'id' | 'options' | 'value' | 'onChange'>) {
  const close = useSelectClose();

  return (
    <FlatSelectContent
      options={options}
      selectedItems={value ? [value] : []}
      onToggle={nextValue => {
        onChange(nextValue);
        close?.();
      }}
      selectId={id}
      selectionMode="single"
    />
  );
}

function MarketShareSelect({ id, label, options, value, onChange }: MarketShareSelectProps) {
  const selectedOption = options.find(option => option.value === value);

  return (
    <Select label={label} value={selectedOption?.label ?? ''} hasValue={Boolean(value)} id={id}>
      <MarketShareOptions id={id} options={options} value={value} onChange={onChange} />
    </Select>
  );
}

export function MarketShareDetailPage() {
  const { messageId } = useParams<{ messageId: string }>();

  const { setDetailPageBreadcrumb } = useBreadcrumbStore();

  const {
    data: message,
    isPending: messageIsPending,
    isSuccess: messageIsSuccess,
    isError: messageIsError,
    error: messageError,
  } = useMessage(messageId);

  const {
    data: analysisDataRaw,
    isError: analysisIsError,
    error: analysisError,
  } = useAnalysisData<MarketshareCategoryEvent>(message?.analysisDataId ?? undefined);
  const categoryOptions: SelectOption<string>[] =
    analysisDataRaw && analysisDataRaw.data
      ? analysisDataRaw.data.map(item => ({
          value: item.key,
          label: `${item.key} - ${item.parentEvent.eventType} - (${item.childEvents.length})`,
        }))
      : [];

  const [category, setCategory] = useState(
    categoryOptions && categoryOptions.length > 0 ? categoryOptions[0]?.value : undefined
  );
  const [childOption, setChildOption] = useState<string>();

  const [childOptions, setChildOptions] = useState<SelectOption<string>[]>([]);

  const [categoryRenderer, setCategoryRenderer] = useState<ColumnChartRenderer>();
  const [childRenderer, setChildRenderer] = useState<ColumnChartRenderer>();

  const location = useLocation();

  const createChartConfig = (label: string) => {
    const barChartConfig = new BarChartConfig();
    barChartConfig.value = {
      label: label,
      unit: MeasureUnitCode.PERCENT,
      interpretation: 1,
    };
    return barChartConfig;
  };

  useEffect(() => {
    setDetailPageBreadcrumb(location.pathname, [{ attribute: 'messageId', values: [messageId ?? ''] }]);
  }, [messageId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [messageId]);

  useEffect(() => {
    const childEvents = category ? (analysisDataRaw?.data.find(item => item.key === category)?.childEvents ?? []) : [];
    const newChildOptions = childEvents.map(ce => ({
      value: ce.itemKey,
      label: `${ce.itemKey} - ${ce.eventType}`,
    }));

    setChildOptions(newChildOptions);
    setChildOption(newChildOptions.length > 0 ? newChildOptions[0].value : undefined);

    if (category && analysisDataRaw?.data) {
      const categoryDeltaBarchartConfig = createChartConfig('Category');
      const categoryGraphData = analysisDataRaw.data
        .find(item => item.key === category)
        ?.parentEvent.combinedPeriodValues.map(cv => new ChartData(cv.periodCode, cv.growthDelta));

      categoryDeltaBarchartConfig.data = [
        new ChartDataArray(ChartDataType.primary, 'Growth Rate Delta', categoryGraphData ?? []),
      ];

      setCategoryRenderer(new ColumnChartRenderer(categoryDeltaBarchartConfig));
    }
    if (childOption && analysisDataRaw?.data) {
      const childDeltaBarchartConfig = createChartConfig('Sub Change');
      const childGraphData = analysisDataRaw.data
        .find(item => item.key === category)
        ?.childEvents.find(ce => ce.itemKey === childOption)
        ?.combinedPeriodValues.map(cv => new ChartData(cv.periodCode, cv.growthDelta));

      childDeltaBarchartConfig.data = [
        new ChartDataArray(ChartDataType.primary, 'Growth Rate Delta', childGraphData ?? []),
      ];

      setChildRenderer(new ColumnChartRenderer(childDeltaBarchartConfig));
    }
  }, [category]);

  useEffect(() => {
    if (childOption && analysisDataRaw?.data) {
      const childDeltaBarchartConfig = createChartConfig('Sub Change');
      const childGraphData = analysisDataRaw.data
        .find(item => item.key === category)
        ?.childEvents.find(ce => ce.itemKey === childOption)
        ?.combinedPeriodValues.map(cv => new ChartData(cv.periodCode, cv.growthDelta));

      childDeltaBarchartConfig.data = [
        new ChartDataArray(ChartDataType.primary, 'Growth Rate Delta', childGraphData ?? []),
      ];

      setChildRenderer(new ColumnChartRenderer(childDeltaBarchartConfig));
    }
  }, [childOption]);

  return (
    <Container maxWidth={false} className="standard-page-container">
      <PageContainer>
        <PageSectionTitle>Nigel</PageSectionTitle>

        {messageIsPending && (
          <Tile className="p-0 bg-snow-50">
            <Box className="flex justify-center py-8">
              <LoadingSpinner />
            </Box>
          </Tile>
        )}
        {messageIsError && <Tile errorDetails={getErrorMessage(messageError)} />}
        {messageIsSuccess && <TradingMessageDisplayTile message={message} />}
        <Tile
          isError={analysisIsError}
          title="Delta in Growth Rate Compared to Peers"
          errorDetails={getErrorMessage(analysisError)}
        >
          <TileContent className="bg-white p-4 rounded-none grid grid-cols-2 gap-2">
            <div>
              <div className="w-[300px]">
                <MarketShareSelect
                  id="marketshare-category-select"
                  label="Category"
                  options={categoryOptions}
                  value={category}
                  onChange={setCategory}
                />
              </div>
              {categoryRenderer && (
                <MetricsChart renderer={categoryRenderer} height="h-[400px]" testId="growth-delta-chart" />
              )}
            </div>
            <div>
              <div className="w-[300px]">
                <MarketShareSelect
                  id="marketshare-child-select"
                  label="Sub Change"
                  options={childOptions}
                  value={childOption}
                  onChange={setChildOption}
                />
              </div>
              {childRenderer && (
                <MetricsChart renderer={childRenderer} height="h-[400px]" testId="growth-delta-chart" />
              )}
            </div>
          </TileContent>
        </Tile>
      </PageContainer>
    </Container>
  );
}
